import { computed } from 'vue'
import { useStore } from '../store'
import { useI18n } from '../i18n'
import { sailingKey, tariffDisplayAmount, tariffDisplayCurrency, tariffTypeI18nKey } from '../../../shared/rules'
import { Direction } from '../../../shared/types/enums'
import type { Availability, Tariff } from '../../../shared/types'
import { humanizeLabel } from '../utils/format'
import { unifiedMeals } from '../utils/meals'

/**
 * Sélection courante du funnel (availability représentative + tarif + places
 * choisis par direction). Getters partagés par BookingSummary et Confirmation
 * (réutilisables ailleurs) — évite la duplication de selectedAv / placesLabel /
 * tariffLabel / amountOf / currencyOf qui vivait en double dans les deux vues.
 */
export function useSelection() {
  const store = useStore()
  const { t } = useI18n()

  // Availability représentative du sailing choisi pour une direction.
  function selectedAv(direction: 'outward' | 'return'): Availability | null {
    const sailingId = store.selectedSailing[direction]
    if (!sailingId) return null
    const list = store.availabilities[direction].all ?? []
    return list.find((av) => sailingKey(av) === sailingId) ?? null
  }

  const outwardAv = computed<Availability | null>(() => selectedAv('outward'))
  const returnAv = computed<Availability | null>(() =>
    store.search.direction === Direction.Return ? selectedAv('return') : null,
  )

  // Tarif retenu pour la direction (groupe 1 = base affichée dans chaque bloc trajet).
  const outwardTariff = computed(() => store.selectedTariffs.outward.group1)
  const returnTariff = computed(() => store.selectedTariffs.return.group1)

  // TOUS les tarifs sélectionnés (aller/retour × groupe 1/2) → base du TOTAL des
  // résumés. Indispensable pour ne pas sous-compter un dossier > 4 passagers.
  const allTariffs = computed<Tariff[]>(() =>
    [
      store.selectedTariffs.outward.group1, store.selectedTariffs.outward.group2,
      store.selectedTariffs.return.group1, store.selectedTariffs.return.group2,
    ].filter((t): t is Tariff => t !== null && t !== undefined),
  )

  // Montant affiché d'un sens = somme des groupes 1 + 2 (display amount). Sans
  // ça, la ligne de prix par trajet sous-compterait un dossier > 4 passagers.
  function directionAmount(direction: 'outward' | 'return'): number {
    const slot = store.selectedTariffs[direction]
    return [slot.group1, slot.group2]
      .filter((tf): tf is Tariff => Boolean(tf))
      .reduce((sum, tf) => sum + tariffDisplayAmount(tf), 0)
  }
  const outwardAmount = computed(() => directionAmount('outward'))
  const returnAmount = computed(() => directionAmount('return'))

  // Tarifs par SOUS-GROUPE d'une direction → affichage des sous-prix par groupe
  // dans le détail prix (dossier > 4 pax).
  function groupPrices(direction: 'outward' | 'return'): Array<{ group: 1 | 2; tariff: Tariff }> {
    const slot = store.selectedTariffs[direction]
    const out: Array<{ group: 1 | 2; tariff: Tariff }> = []
    if (slot.group1) out.push({ group: 1, tariff: slot.group1 })
    if (slot.group2) out.push({ group: 2, tariff: slot.group2 })
    return out
  }
  const outwardGroupPrices = computed(() => groupPrices('outward'))
  const returnGroupPrices = computed(() => groupPrices('return'))

  // Places choisies d'une direction, groupe 1 + groupe 2 (si > 4 pax) jointes.
  function placesLabel(direction: 'outward' | 'return'): string {
    const slot = store.selectedPlaces[direction]
    return [slot.group1, slot.group2]
      .filter((p): p is NonNullable<typeof p> => Boolean(p))
      .map((p) => humanizeLabel(p.name || p.code))
      .join(' + ')
  }
  const outwardPlacesLabel = computed(() => placesLabel('outward'))
  const returnPlacesLabel = computed(() => placesLabel('return'))

  // Repas sélectionnés (quantité > 0) PAR direction, via les règles SHARED (même
  // source que l'étape Options). Dispatchés dans leur bloc trajet respectif
  // (résumé) ; `selectedMeals` les concatène pour le détail du prix / le total.
  function mealRowsFor(
    direction: 'outward' | 'return',
  ): Array<{ key: string; label: string; quantity: number; amount: number; currency: string }> {
    const rows: Array<{ key: string; label: string; quantity: number; amount: number; currency: string }> = []
    for (const m of unifiedMeals(store, direction)) {
      if (m.quantity > 0) {
        rows.push({
          key: `${direction}-${m.key}`,
          label: humanizeLabel(m.name || m.code),
          quantity: m.quantity,
          amount: m.amount * m.quantity,
          currency: m.currency,
        })
      }
    }
    return rows
  }
  const outwardMeals = computed(() => mealRowsFor('outward'))
  const returnMeals = computed(() => (store.search.direction === Direction.Return ? mealRowsFor('return') : []))
  const selectedMeals = computed(() => [...outwardMeals.value, ...returnMeals.value])

  // Total repas du dossier (Σ prix × quantité) — additionné au total tarifs.
  const mealsAmount = computed(() => selectedMeals.value.reduce((sum, m) => sum + m.amount, 0))

  // Libellé i18n d'un type de tarif (ticket / presale / option) ; brut sinon.
  function tariffLabel(type: string): string {
    const key = tariffTypeI18nKey(type)
    return key ? t(`tariff.${key}`) : type
  }
  const amountOf = (tariff: Tariff): number => tariffDisplayAmount(tariff)
  const currencyOf = (tariff: Tariff): string => tariffDisplayCurrency(tariff)

  return {
    outwardAv,
    returnAv,
    outwardTariff,
    returnTariff,
    allTariffs,
    outwardAmount,
    returnAmount,
    outwardGroupPrices,
    returnGroupPrices,
    outwardPlacesLabel,
    returnPlacesLabel,
    outwardMeals,
    returnMeals,
    selectedMeals,
    mealsAmount,
    tariffLabel,
    amountOf,
    currencyOf,
  }
}
